import { AppId, FCX, getEnding, getFormattedThousand, usePlatformContext } from "@datassential/platform-ui-lib";
import AccessLink from "@components/AccessLink/AccessLink";
import FoodSectionLaunches from "@pages/Food/components/SectionLaunches/SectionLaunches";
import FoodSectionTitle from "@pages/Food/refresh/components/FoodSectionTitle/FoodSectionTitle";
import { LOCKED_LAUNCHES_PREVIEW_ITEMS } from "./FoodLaunchesSection.constants";
import "./FoodLaunchesSection.scss";

interface Props {
    word: string;
    setHasData: (hasData: boolean) => void;
}

/**
 * Launches section of the refreshed Food Profile: the shared Launches section with the refresh section title
 * (tabs after the gradient rule) and a "More Launches & Ratings" link next to the search. Restyling lives in the SCSS.
 */
const FoodLaunchesSection: FCX<Props> = ({ word, setHasData }) => {
    const { userInfo: { products } } = usePlatformContext();
    const launchesAndRatings = products[AppId.LaunchesAndRatings];
    const hasLaunchesAndRatings = !launchesAndRatings?.isDisabled;
    // With access but no URL there is nowhere to go: no link (same rule as "More Menu Trends").
    const hasMoreLink = !hasLaunchesAndRatings || !!launchesAndRatings?.url;

    const renderLockedMessage = (itemsCount: number) => (
        <div data-testid="textFoodLaunchesPermissionOverlayContentLine-0">
            {itemsCount > 0 ? (
                <>
                    Upgrade your subscription to unlock these <b>{getFormattedThousand(itemsCount)} new {getEnding('item', itemsCount)}</b> featuring <b>{word.toUpperCase()}</b>.
                </>
            ) : (
                <>
                    Upgrade your subscription to unlock new items featuring <b>{word.toUpperCase()}</b>.
                </>
            )}
        </div>
    );

    return (
        <div className="FoodLaunchesSection" data-testid="blockFoodLaunches">
            <FoodSectionLaunches
                word={word}
                setHasData={setHasData}
                lockedPreview={hasLaunchesAndRatings ? undefined : {
                    items: LOCKED_LAUNCHES_PREVIEW_ITEMS,
                    renderMessage: renderLockedMessage,
                }}
                renderHeader={(tabs) => (
                    <FoodSectionTitle className="FoodLaunchesSection__title" title={`Launches: ${word}`}>
                        <div className="FoodLaunchesSection__divider" role="separator" aria-orientation="vertical"/>
                        {tabs}
                    </FoodSectionTitle>
                )}
                // At the top, next to the search: the list keeps loading more items as the user scrolls.
                searchAction={hasMoreLink && (
                    <AccessLink
                        className="FoodLaunchesSection__more"
                        url={launchesAndRatings?.url || null}
                        text="More Launches & Ratings"
                        hasAccess={hasLaunchesAndRatings}
                        // Figma: without access only the locked link; the overlay holds the single "Contact us".
                        hideAccessMessage
                        testId="linkFoodLaunchesMoreLaunches"
                    />
                )}
            />
        </div>
    );
};

export default FoodLaunchesSection;
